import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lightbulb, X } from 'lucide-react';
import { hintFor } from '@dental-flow/rbac';
import { authApi, type LookPrefs } from '@/api/auth.api';
import { useSession } from './session';
import './ScreenHint.css';

/**
 * „Aici faci asta" — o propoziție, prima dată când omul ajunge pe un ecran.
 *
 * Alternativa evidentă era un tur la primul login, și e tiparul pe care
 * teardown-urile din 2026 îl numesc depășit: rulează înainte ca omul să aibă
 * o treabă în cap, se dă Next de șase ori, nu rămâne nimic — iar o oprire
 * care arată spre un buton mutat între timp e un ecran care declară ceva ce
 * nu face. Aici indicia apare **când omul e deja acolo**, cu sarcina în
 * minte, și dispare definitiv când o citește.
 *
 * Fiecare om are lista lui (`staff.look_prefs.hintsSeen`), deci o colegă care
 * începe mâine le primește pe toate, iar cine lucrează de un an nu vede
 * niciuna după ce le-a închis. Se aduc toate înapoi din Setări → Aspectul meu.
 *
 * **Pe clinica demo lista stă în browser**, nu pe cont: conturile demo-ului
 * sunt publice și comune, iar un vizitator care închide o indicie ar închide-o
 * pentru următorul. Aceeași alegere ca la vederea demo (`demo-view.ts`), din
 * același motiv — și demo-ul e tocmai locul unde cineva vede produsul prima
 * oară, deci acolo indiciile au cel mai mult de spus.
 *
 * Inelul de pe buton e un adaos: dacă selectorul se găsește, butonul despre
 * care vorbește indicia primește un contur cât timp ea e pe ecran; dacă
 * ecranul s-a schimbat și nu se găsește, indicia rămâne întreagă.
 */
const DEMO_KEY = 'df.hintsSeen';

/**
 * Ce a citit vizitatorul demo-ului, în browserul lui.
 *
 * Citirea poate și să arunce (fereastră privată, date de sit blocate), deci
 * totul e într-un `try`: fără listă, indiciile se arată — ceea ce e starea
 * normală pentru cineva care tocmai a intrat, nu o eroare.
 */
function readLocal(): string[] {
  try {
    const raw = window.localStorage.getItem(DEMO_KEY);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeLocal(list: string[]): void {
  try {
    window.localStorage.setItem(DEMO_KEY, JSON.stringify(list));
  } catch {
    // Un browser care nu ne lasă să ținem minte arată indicia din nou. E o
    // stare validă, nu un motiv de eroare pe ecran.
  }
}

export function ScreenHint() {
  const { t } = useTranslation('dashboard');
  const { pathname } = useLocation();
  const { user, tenant, can, hasFeature } = useSession();
  const qc = useQueryClient();
  const [hidden, setHidden] = useState<string[]>([]);
  const ringed = useRef<Element | null>(null);

  const staff = user?.kind === 'staff';
  const demo = !!tenant?.demo;
  const prefs = useQuery({
    queryKey: ['auth', 'look'],
    queryFn: authApi.lookPrefs,
    enabled: staff && !demo,
    staleTime: 5 * 60_000,
  });

  const seen = demo ? readLocal() : (prefs.data?.hintsSeen ?? []);
  const hint =
    staff && (demo || !prefs.isPending)
      ? hintFor(pathname, { seen: [...seen, ...hidden], can, hasFeature })
      : null;

  const read = useMutation({
    mutationFn: async (key: string) => {
      if (demo) {
        writeLocal([...seen, key]);
        return null;
      }
      return authApi.setLookPrefs({ hintsSeen: [...seen, key] });
    },
    onSuccess: (saved) => {
      if (saved) qc.setQueryData<LookPrefs>(['auth', 'look'], saved);
    },
  });

  /*
   * Inelul se pune pe butonul vizat cât timp indicia e pe ecran, și se ia
   * înapoi la plecare — inclusiv dacă omul schimbă pagina cu indicia
   * deschisă. Selectorul care nu se găsește nu e o eroare: ecranele se
   * rescriu, iar indicia trebuie să supraviețuiască rescrierii lor.
   */
  useEffect(() => {
    ringed.current?.classList.remove('hint-ring');
    ringed.current = null;
    if (!hint?.anchor) return;

    /*
     * Butonul poate să nu existe încă: ecranul își încarcă datele, iar bara
     * lui de acțiuni apare după. Se caută de câteva ori, la o jumătate de
     * secundă, și apoi se renunță — o indicie fără inel e o indicie
     * întreagă, iar o căutare fără capăt ar fi un cronometru care rulează pe
     * fiecare ecran, pentru un contur.
     */
    const anchor = hint.anchor;
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.querySelector(anchor);
      if (el) {
        el.classList.add('hint-ring');
        ringed.current = el;
        window.clearInterval(timer);
      } else if (++tries > 6) {
        window.clearInterval(timer);
      }
    }, 500);

    return () => {
      window.clearInterval(timer);
      ringed.current?.classList.remove('hint-ring');
      ringed.current = null;
    };
  }, [hint]);

  if (!hint) return null;

  return (
    <aside className="hint" role="note">
      <span className="hint__icon" aria-hidden>
        <Lightbulb size={15} />
      </span>
      <p className="hint__text">{t(`hint_${hint.key}`)}</p>
      <button
        type="button"
        className="hint__ok"
        onClick={() => {
          // Închisă pe loc; scrierea pe cont vine după și n-o mai aduce înapoi.
          setHidden((h) => [...h, hint.key]);
          read.mutate(hint.key);
        }}
      >
        {t('amInteles')}
      </button>
      <button
        type="button"
        className="hint__x"
        title={t('maiTarziu')}
        aria-label={t('maiTarziu')}
        onClick={() => setHidden((h) => [...h, hint.key])}
      >
        <X size={14} aria-hidden />
      </button>
    </aside>
  );
}
